import React from "react";
import { Col, Row, Label, Form } from "reactstrap";
import shallow from "zustand/shallow";
import useCurrentAccountStore from "./AccountStore";
import tabOnEnter from "../utils/formNavigationUtils";
import InputElement, {
	CurrencyInput,
} from "../features/forms/components/InputElement";
import dayjs from "dayjs";
import { getPayoffMethodLabels } from "./accountLabelUtils";

const AccountBalance = () => {
	const { account = {}, lease } = useCurrentAccountStore(
		(state) => state,
		shallow
	);
	const labels = getPayoffMethodLabels(account?.payoffMethod);
	const isLease = account?.payoffMethod === 4;
	const isRTO = account?.payoffMethod === 5;

	return (
		<>
			<Form>
				<Col className="pb-3 px-0 d-flex align-items-center justify-content-center">
					<h4>Balance({dayjs(account.lastUpdateDate).format("MM/DD/YYYY")})</h4>
				</Col>

				<Row className="mx-0 pt-0 d-none d-md-flex">
					<Col xs="12" md="4" lg="3" className="mb-2">
						<Label></Label>
					</Col>
					<Col xs="12" md="4" lg="3" className="mb-2">
						<Label className="bold">Total</Label>
					</Col>
					<Col xs="12" md="4" lg="3" className="mb-2">
						<Label className="bold">Paid To Date</Label>
					</Col>
					<Col xs="12" md="4" lg="3" className="mb-2">
						<Label className="bold">Remainder</Label>
					</Col>
				</Row>

				<Row className="mx-0 pt-0" onKeyDown={tabOnEnter}>
					<Col xs="12" md="4" lg="3" className="mb-2">
						<Label className="d-md-none d-block">{labels.principleLoan}</Label>
						<Label className="d-none d-md-block">{labels.principleLoan}</Label>
					</Col>
					<Col xs="12" md="4" lg="3" className="mb-2">
						<Label className="d-md-none">Total</Label>
						<CurrencyInput
							value={account.amtFinanced}
							readOnly={true}
							name="amtFinanced"
							type="Currency"
							disableValidation
							colSize="col-xl-12"
						/>
					</Col>
					<Col xs="12" md="4" lg="3" className="mb-2">
						<Label className="d-md-none">Paid To Date</Label>
						<CurrencyInput
							value={account.principle}
							readOnly={true}
							name="principle"
							type="Currency"
							disableValidation
							colSize="col-xl-12"
						/>
					</Col>
					<Col xs="12" md="4" lg="3" className="mb-2">
						<Label className="d-md-none">Remainder</Label>
						<CurrencyInput
							value={account.principleRemaining}
							readOnly={true}
							name="principleRemaining"
							type="Currency"
							disableValidation
							colSize="col-xl-12"
							style={{ fontWeight: "bold" }}
						/>
					</Col>
				</Row>
				{account.financeCharge > 0 && !isRTO && (
					<Row className="mx-0 pt-0" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label>{labels.financeCharge}</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Total</Label>
							<CurrencyInput
								value={account.financeCharge}
								readOnly={true}
								name="financeCharge"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Paid To Date</Label>
							<CurrencyInput
								value={account.interest - account.penaltyInt}
								readOnly={true}
								name="interest"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Remainder</Label>
							<CurrencyInput
								value={
									account.financeCharge -
									(account.interest - account.penaltyInt)
								}
								readOnly={true}
								name="remFinanceCharge"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
					</Row>
				)}

				{(account.isTaxDeferred || account.monthlyTax > 0) && (
					<Row className="mx-0 pt-0" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label>Tax</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Total</Label>
							<CurrencyInput
								value={account.tax}
								readOnly={true}
								name="tax"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Paid To Date</Label>
							<CurrencyInput
								value={account.taxPaid}
								readOnly={true}
								name="taxPaid"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Remainder</Label>
							<CurrencyInput
								value={account.tax - account.taxPaid}
								readOnly={true}
								name="remainTax"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
					</Row>
				)}

				{/* For Lease/RTO: Show Balance row before Late/Adjustment */}
				{(isLease || isRTO) && (
					<Row className="mx-0 pt-0 fw-bold" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="fw-bold">{labels.balance}</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none fw-bold">Total</Label>
							<CurrencyInput
								value={
									account.amtFinanced +
									(isLease ? account.financeCharge : 0) +
									(account.isTaxDeferred || account.monthlyTax > 0 ? account.tax : 0)
								}
								readOnly={true}
								name="leaseRtoTotal"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none fw-bold">Paid To Date</Label>
							<CurrencyInput
								value={
									account.principle +
									(isLease ? account.interest : 0) +
									(account.isTaxDeferred || account.monthlyTax > 0 ? account.taxPaid : 0)
								}
								readOnly={true}
								name="leaseRtoPaid"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none fw-bold">Remainder</Label>
							<CurrencyInput
								value={account.balance}
								readOnly={true}
								name="leaseRtoBalance"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold", backgroundColor: "lightblue" }}
							/>
						</Col>
					</Row>
				)}

				{/* Adjustment row - for Lease/RTO only show if > 0 */}
				{((isLease || isRTO) ? account.debitCreditTotal > 0 : true) && (
					<Row className="mx-0 pt-0" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label>Adjustment</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Total</Label>
							<CurrencyInput
								value={account.debitCreditTotal}
								readOnly={true}
								name="debitCreditTotal"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Paid To Date</Label>
							<CurrencyInput
								value={account.adjPaid}
								readOnly={true}
								name="adjPaid"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Remainder</Label>
							<CurrencyInput
								value={account.totalDebitCredit}
								readOnly={true}
								name="totalDebitCredit"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
					</Row>
				)}

				{/* Late Fee row - for Lease/RTO only show if > 0 */}
				{((isLease || isRTO) ? account.totalLateFees > 0 : true) && (
					<Row className="mx-0 pt-0" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label>Late Fee</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Total</Label>
							<CurrencyInput
								value={account.totalLateFees}
								readOnly={true}
								name="totalLateFees"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Paid To Date</Label>
							<CurrencyInput
								value={account.latePaid}
								readOnly={true}
								name="lateFeesPaid"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Remainder</Label>
							<CurrencyInput
								value={account.lateFeeDue}
								readOnly={true}
								name="lateFeeDue"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
					</Row>
				)}

				{account.pickupTotal > 0 && (
					<Row className="mx-0 pt-0" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label>Deferred (Pickup)</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Total</Label>
							<CurrencyInput
								value={account.pickupTotal}
								readOnly={true}
								name="pickupTotal"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Paid To Date</Label>
							<CurrencyInput
								value={account.pickupPaid}
								readOnly={true}
								name="pickupPaid"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Remainder</Label>
							<CurrencyInput
								value={account.pickupTotal - account.pickupPaid}
								readOnly={true}
								name="pickupRemain"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
					</Row>
				)}
				{account.penaltyInt > 0 && !isRTO && !isLease && (
					<Row className="mx-0 pt-0" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label>{labels.additionalInterest}</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Total</Label>
							<CurrencyInput
								readOnly={true}
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Paid To Date</Label>
							<CurrencyInput
								value={account.penaltyInt}
								readOnly={true}
								name="َAditional Interest"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none">Remainder</Label>
							<CurrencyInput
								readOnly={true}
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
					</Row>
				)}

				{/* Payoff row for Lease/RTO - only show if there's late fee, adjustment, or pickup remaining */}
				{(isLease || isRTO) && (account.lateFeeDue > 0 || account.totalDebitCredit > 0 || (account.pickupTotal - account.pickupPaid) > 0) && (
					<Row className="mx-0 pt-0 fw-bold" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="fw-bold">Payoff</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none fw-bold">Remainder</Label>
							<CurrencyInput
								value={account.payoff}
								readOnly={true}
								name="payoffRemainder"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold", backgroundColor: "lightgreen" }}
							/>
						</Col>
					</Row>
				)}

				{/* Total row - only for Loan accounts (not Lease/RTO) */}
				{!isLease && !isRTO && (
					<Row className="mx-0 pt-0 fw-bold" onKeyDown={tabOnEnter}>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="fw-bold">Total</Label>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none fw-bold">Total</Label>
							<CurrencyInput
								value={
									account.amtFinanced +
									account.financeCharge +
									account.debitCreditTotal +
									account.totalLateFees +
									account.pickupTotal +
									(account.isTaxDeferred || account.monthlyTax > 0
										? account.tax
										: 0)
								}
								readOnly={true}
								name="totalToPay"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none fw-bold">Paid To Date</Label>
							<CurrencyInput
								value={
									account.principle +
									account.interest +
									account.adjPaid +
									account.latePaid +
									account.pickupPaid +
									(account.isTaxDeferred || account.monthlyTax > 0
										? account.taxPaid
										: 0)
								}
								readOnly={true}
								name="totalPayToDate"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
						<Col xs="12" md="4" lg="3" className="mb-2">
							<Label className="d-md-none fw-bold">Remainder</Label>
							<CurrencyInput
								value={account.balance}
								readOnly={true}
								name="totalRemainder"
								type="Currency"
								disableValidation
								colSize="col-xl-12"
								style={{ fontWeight: "bold" }}
							/>
						</Col>
					</Row>
				)}
				{!isLease && !isRTO && (
					<Row className="mx-0 pt-0" onKeyDown={tabOnEnter}>
						{/* Label Column */}
						<Col xs="12" md="2" lg="2" className="mb-2">
							<Label>Break Even</Label>
						</Col>

						{/* Date, Cost, and Break Even Inputs */}
						<Col xs="12" md="10" lg="10" className="mb-2">
							<Row>
								{/* Date Input */}
								<Col xs="12" md="4" lg="4" className="mb-2">
									<InputElement
										disableValidation
										readOnly={true}
										value={account.breakEven}
										name="breakEvenDate"
										label="Date"
										type="date"
										colSize="col-12"
									/>
								</Col>

								{/* Cost Input */}
								<Col xs="12" md="4" lg="4" className="mb-2">
									<CurrencyInput
										disableValidation
										label="Cost"
										readOnly={true}
										type="currency"
										name="cost"
										value={account.totalCost}
										colSize="col-12"
									/>
								</Col>

								{/* Break Even Input */}

								<Col xs="12" md="4" lg="4" className="mb-2">
									<CurrencyInput
										disableValidation
										label="Break Even"
										readOnly={true}
										type="currency"
										name="breakEven"
										value={
											account.totalCost - account.principle > 0
												? account.totalCost - account.principle
												: 0
										}
										colSize="col-12"
									/>
								</Col>
							</Row>
						</Col>
					</Row>
				)}
			</Form>
		</>
	);
};

export default AccountBalance;
